import { useState } from 'react';
import { apiFetch } from '../../../context/AuthContext';
import { useProfile } from '../../../hooks/useProfile';
import { formatTime, getInitials, getTrilateralName } from '../utils/formatters';
import { buildSystemMessageText, isSystemMessageType, parseSystemMessagePayload } from '../utils/systemMessages';
import {
  extractInfinityCode,
  extractUserEmail,
  extractUserName,
  getUserInfoFromLocalStorage,
} from '../utils/userHelpers';

const API_BASE_URL = process.env.REACT_APP_BASE_URL || 'https://nexploy-eg.com';

// Same fix as useMessages.js's textFor(), applied to the sidebar's
// "last message" preview: a group whose most recent message is a
// system.* row (e.g. right after creation) otherwise shows the raw JSON
// payload as the preview text instead of a real sentence.
const lastMessagePreview = (content, type, t) =>
  isSystemMessageType(type) ? buildSystemMessageText(type, parseSystemMessagePayload(content), t) : (content || '');

const useMessengerInit = ({
  currentUser,
  setCurrentUser,
  conversations,
  setConversations,
  messages,
  setMessages,
  onlineUsers,
  employeeProfilePics,
  fetchEmployeeProfilePic,
  fetchMessagesForUser,
  isSameUser,
  language,
  t,
}) => {
  const { profile } = useProfile();
  const [token, setToken] = useState(null);
  const [allEmployees, setAllEmployees] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userNamesCache, setUserNamesCache] = useState({});
  // Starts true (not false) so the page-level skeleton is guaranteed to be
  // visible on first paint instead of racing the mount effect that flips it —
  // otherwise the real (empty) page flashes briefly before the skeleton, then
  // the skeleton flashes before the real data arrives.
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchAllEmployees = async (authToken, user) => {
    try {
      const clientName = profile?.clientName || localStorage.getItem('clientName') || '';

      // Always include clientName in the query so the backend can resolve visibility.
      // Fall back to the user's own employee record on the server side if it's empty.
      const params = new URLSearchParams();
      if (clientName && clientName.toLowerCase() !== 'all') {
        params.set('clientName', clientName);
      }
      // Identity-only payload. The mapper below reads exactly: infinityCode,
      // sr, arabicName, email, clientName, department/location, position --
      // all of which the backend's EMPLOYEE_BASIC_SELECT returns. Requesting
      // the full record here pulled 87 columns per employee for every user at
      // app start, a primary driver of the backend's memory pressure.
      // If you add a field to the mapper below, add it to
      // EMPLOYEE_BASIC_SELECT in user-service/src/services/employeeService.js.
      params.set('fields', 'basic');
      const queryString = params.toString() ? `?${params.toString()}` : '';
      const endpoint = `/employees/visibility${queryString}`;

      // Tell the backend which view type this request is for so it picks the
      // correct visibility branch (admin / manager / user). Must read the
      // account's CURRENT view toggle (managerViewType/viewType), not the
      // immutable base DB role — a promoted user/manager holding an
      // AdminLevelVisibilityRule scope only gets that scope applied when the
      // request says viewType: 'admin'. Reading base `role` instead (the old
      // bug here) made a promoted user/manager in Admin View silently fall
      // back to "every colleague in the company" / "my reporting hierarchy",
      // bypassing their configured visibility restriction entirely — this is
      // the same convention MessengerPage.jsx already uses for its own
      // permission gates (isAdminViewGated), just replicated here since this
      // hook can't easily share that component-local variable.
      const viewType = localStorage.getItem('managerViewType') || localStorage.getItem('viewType') || 'user';

      const response = await apiFetch(`${API_BASE_URL}${endpoint}`, {
        method: 'GET',
        headers: { 'x-view-type': viewType },
      });
      if (!response.ok) throw new Error('Failed to fetch employees');
      const data = await response.json();
      const employeesData = data.employees || data;

      const employees = employeesData
        .map((emp) => {
          const empInfinityCode = extractInfinityCode(emp);
          if (!empInfinityCode) return null;

          const empName = extractUserName(emp, language);
          const empNameEn = extractUserName(emp, 'en');
          const empEmail = extractUserEmail(emp);

          return {
            id: empInfinityCode,
            infinityCode: empInfinityCode,
            name: getTrilateralName(empNameEn), // Force English name
            englishName: empNameEn,
            arabicName: emp.arabicName || emp.sr || '',
            email: empEmail,
            avatar: getInitials(empNameEn),
            company: emp.company || emp.Company || emp.clientName || emp.organization || emp.org || '',
            department: emp.department || emp.location || emp['Location'] || '',
            position: emp.position || emp.employeeType || emp['Employee Type'] || '',
          };
        })
        .filter((emp) => emp && !isSameUser(emp.id, user?.infinityCode));

      setAllEmployees(employees);

      const namesCache = {};
      employees.forEach((emp) => {
        namesCache[emp.id] = emp.name;
      });
      setUserNamesCache(namesCache);
    } catch (err) {
      console.error('[fetchAllEmployees] error:', err);
      setError('Failed to load employees');
    }
  };

  const fetchConversations = async (authToken, user) => {
    try {
      setLoading(true);
      setError(null);

      const response = await apiFetch(`${API_BASE_URL}/api/private-messages/conversations`, {
        method: 'GET',
      });

      if (!response.ok) throw new Error('Failed to fetch conversations');

      const data = await response.json();
      let conversationsArray = Array.isArray(data) ? data : (data.conversations || data.data || []);

      if (conversationsArray.length === 0) {
        setConversations([]);
        return;
      }

      const mappedConversations = conversationsArray
        .map((conv) => {
          const otherUser = conv.user || conv.otherUser || {};

          const otherUserInfinityCode = extractInfinityCode(otherUser);
          const otherUserId = otherUserInfinityCode !== null && otherUserInfinityCode !== undefined
            ? String(otherUserInfinityCode)
            : null;

          if (!otherUserId) return null;
          if (isSameUser(otherUserId, user?.infinityCode)) return null;

          const userNameEn = otherUser.sr || otherUser.name || userNamesCache[otherUserId] || `User ${otherUserId}`;

          return {
            id: otherUserId,
            name: getTrilateralName(userNameEn), // Force English
            englishName: userNameEn,
            arabicName: otherUser.arabicName || otherUser.sr || '',
            avatar: getInitials(userNameEn),
            company: otherUser.company || otherUser.Company || otherUser.clientName || otherUser.organization || otherUser.org || '',
            department: otherUser.department || otherUser.location || otherUser['Location'] || '',
            position: otherUser.position || otherUser.employeeType || otherUser['Employee Type'] || '',
            email: otherUser.email || otherUser.emailAddress || otherUser.email_address || '',
            lastMessage: conv.lastMessage || conv.lastMessageContent || '',
            isFromMe: !!conv.isFromMe,
            time: formatTime(conv.lastMessageTime || new Date(), t),
            timestamp: new Date(conv.lastMessageTime || new Date()).getTime(),
            unread: conv.unreadCount || 0,
            online: onlineUsers.has(otherUserId),
          };
        })
        .filter((conv) => conv && conv.id && !Number.isNaN(Number(conv.id)) && !String(conv.id).startsWith('group-'));

      // Fetch active groups
      const groupsResponse = await apiFetch(`${API_BASE_URL}/api/chat-groups`, { method: 'GET' });
      let mappedGroups = [];
      if (groupsResponse.ok) {
        const groupsData = await groupsResponse.json();
        // Only include non-archived groups here; archived ones are fetched separately below
        mappedGroups = (Array.isArray(groupsData) ? groupsData : [])
          .filter(group => !group.isArchived)
          .map(group => ({
            id: `group-${group.id}`, // Add group- prefix for frontend logic
            name: group.name,
            englishName: group.name,
            arabicName: group.name,
            avatar: getInitials(group.name),
            isGroup: true,
            company: '',
            department: '',
            position: '',
            email: '',
            lastMessage: lastMessagePreview(group.lastMessageContent, group.lastMessageType, t),
            time: formatTime(group.lastMessageTime || group.created_at, t),
            timestamp: new Date(group.lastMessageTime || group.created_at).getTime(),
            unread: 0,
            online: false,
            members: group.members || [],
            role: group.role,
            createdBy: group.createdBy,
            membershipMode: group.membershipMode || 'MANUAL',
            postingMode: group.postingMode || 'EVERYONE',
            syncDepartmentId: group.syncDepartmentId ?? null,
            syncWorkingCompany: group.syncWorkingCompany ?? null,
            autoAddNewMembers: group.autoAddNewMembers ?? true,
            isArchived: false
          }));
      }

      // Fetch archived groups
      const archivedResponse = await apiFetch(`${API_BASE_URL}/api/chat-groups/archived`, { method: 'GET' });
      let mappedArchived = [];
      if (archivedResponse.ok) {
        const archivedData = await archivedResponse.json();
        mappedArchived = (Array.isArray(archivedData) ? archivedData : []).map(group => ({
          id: `group-${group.id}`,
          name: group.name,
          englishName: group.name,
          arabicName: group.name,
          avatar: getInitials(group.name),
          isGroup: true,
          company: '',
          department: '',
          position: '',
          email: '',
          lastMessage: lastMessagePreview(group.lastMessageContent, group.lastMessageType, t),
          time: formatTime(group.lastMessageTime || group.created_at, t),
          timestamp: new Date(group.lastMessageTime || group.created_at).getTime(),
          unread: 0,
          online: false,
          members: group.members || [],
          role: group.role,
          createdBy: group.createdBy,
          membershipMode: group.membershipMode || 'MANUAL',
          postingMode: group.postingMode || 'EVERYONE',
          syncDepartmentId: group.syncDepartmentId ?? null,
          syncWorkingCompany: group.syncWorkingCompany ?? null,
          autoAddNewMembers: group.autoAddNewMembers ?? true,
          isArchived: true
        }));
      }

      const allConversations = [...mappedConversations, ...mappedGroups, ...mappedArchived];
      allConversations.sort((a, b) => b.timestamp - a.timestamp);
      setConversations(allConversations);
    } catch (err) {
      console.error("Error fetching conversations/groups:", err);
      setConversations([]);
    } finally {
      setLoading(false);
    }
  };

  const initializeMessaging = async (initializeSocket) => {
    try {
      const authToken = localStorage.getItem('token');
      if (!authToken) {
        setError('Authentication required. Please log in.');
        setLoading(false);
        return null;
      }
      setToken(authToken);

      const userInfo = getUserInfoFromLocalStorage(profile);
      if (!userInfo) {
        setError('User identification failed. Please re-login.');
        setLoading(false);
        return null;
      }

      const infinityCode = extractInfinityCode(userInfo);
      if (!infinityCode) {
        setError('User identification failed. Please re-login.');
        setLoading(false);
        return null;
      }

      const userName = extractUserName(userInfo, language);
      const userNameEn = extractUserName(userInfo, 'en');
      const userEmail = extractUserEmail(userInfo);

      const user = {
        id: infinityCode,
        infinityCode,
        name: userName,
        email: userEmail,
        avatar: getInitials(userNameEn),
      };

      setCurrentUser(user);

      if (initializeSocket) {
        // The socket has its own reconnection logic (see useSocket.js) and
        // may reject on the very first connect_error (a transient network
        // blip, a slow TLS handshake, a proxy dropping the websocket
        // upgrade) even though it goes on to connect moments later. That
        // must NOT block loading employees/conversations below — otherwise
        // a single flaky first attempt leaves allEmployees permanently
        // empty (nothing to search in "New Group") with no retry, since
        // nothing re-invokes this function once the socket reconnects.
        try {
          await initializeSocket(authToken, user);
          await new Promise((resolve) => setTimeout(resolve, 1000));
        } catch (socketErr) {
          console.error('[initializeMessaging] socket connect failed, continuing without it:', socketErr);
        }
      }

      await Promise.all([
        fetchAllEmployees(authToken, user),
        fetchConversations(authToken, user),
      ]);

      return { authToken, user };
    } catch (err) {
      setError(`Failed to initialize messaging: ${err.message}`);
      // Safety net: if initializeSocket (or anything before fetchConversations
      // runs its own try/finally) throws, loading would otherwise never
      // resolve to false and the skeleton would be stuck forever.
      setLoading(false);
      return null;
    }
  };

  const startNewConversation = async (employee) => {
    if (!employee.id || !currentUser?.infinityCode) {
      alert(t.userInfoMissing);
      return;
    }

    if (isSameUser(employee.id, currentUser?.infinityCode)) {
      alert(t.cannotMessageSelf);
      return;
    }

    const existingConv = conversations.find((conv) => isSameUser(conv.id, employee.id));
    if (existingConv) {
      await handleUserSelect(existingConv);
      return;
    }

    setSelectedUser({
      id: employee.id,
      name: getTrilateralName(employee.name),
      arabicName: employee.arabicName || employee.sr || '',
      avatar: employee.avatar,
      company: employee.company || employee.Company || employee.clientName || employee.organization || employee.org || '',
      department: employee.department || employee.location || employee['Location'] || '',
      position: employee.position || employee.employeeType || employee['Employee Type'] || '',
      email: employee.email || employee.emailAddress || employee.email_address || '',
      online: onlineUsers.has(employee.id),
    });

    if (!employeeProfilePics[employee.id]) {
      await fetchEmployeeProfilePic(employee.id);
    }

    setMessages((prev) => ({ ...prev, [employee.id]: [] }));
  };

  const handleUserSelect = async (user) => {
    setSelectedUser({
      id: user.id,
      name: getTrilateralName(user.name),
      arabicName: user.arabicName || user.sr || '',
      avatar: user.avatar,
      company: user.company || user.Company || user.clientName || user.organization || user.org || '',
      department: user.department || user.location || user['Location'] || '',
      position: user.position || user.employeeType || user['Employee Type'] || '',
      email: user.email || user.emailAddress || user.email_address || '',
      online: user.online,
    });

    if (!employeeProfilePics[user.id]) {
      await fetchEmployeeProfilePic(user.id);
    }

    if (!messages[user.id] || messages[user.id].length === 0) {
      await fetchMessagesForUser(user.id);
    }

    setConversations((prev) => prev.map((c) => (
      isSameUser(c.id, user.id) ? { ...c, unread: 0 } : c
    )));
  };

  return {
    token,
    allEmployees,
    setAllEmployees,
    selectedUser,
    setSelectedUser,
    initializeMessaging,
    startNewConversation,
    handleUserSelect,
    userNamesCache,
    setUserNamesCache,
    fetchAllEmployees,
    fetchConversations,
    loading,
    error,
    setError,
  };
};

export default useMessengerInit;